// API client tipado pra `/api/crm/leads` (Story 11.2 — primeira feature CRM).
// Padrão: wrapper sobre `apiClient` (axios) + Zod parse nas respostas (Tier 2).
//
// Fonte de tipos: schemas Zod do `@control-center/shared` (back+front).
// Convenção de envelope do backend:
//  - lista: { items, meta: { next_cursor, total } }
//  - single: { data }
//  - create: { data, warnings: [...] }  (dedup-warning FR-50.9)

import { z } from 'zod';

import {
  crmLeadDuplicateWarningSchema,
  crmLeadOriginSchema,
  crmLeadPotentialSchema,
  crmLeadStatusSchema,
  crmLeadTemperatureSchema,
  type CreateCrmLeadDto,
  type CrmLeadDuplicateWarning,
  type DiscardCrmLeadDto,
  type UpdateCrmLeadDto,
} from '@control-center/shared';

import { apiClient } from './api-client';

// -----------------------------------------------------------------------------
// Schemas de resposta (Tier 2 — valida o shape que o backend devolve)
// -----------------------------------------------------------------------------

const relationSchema = z.object({ id: z.string(), name: z.string() }).nullable();

const crmLeadSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  company: z.string().nullable(),
  segment: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  origin: crmLeadOriginSchema,
  saas_product_id: z.string().nullable(),
  pain_point: z.string().nullable(),
  potential: crmLeadPotentialSchema,
  temperature: crmLeadTemperatureSchema,
  responsible_user_id: z.string().nullable(),
  next_action: z.string().nullable(),
  next_contact_date: z.string().nullable(),
  status: crmLeadStatusSchema,
  discard_reason: z.string().nullable(),
  client_id: z.string().nullable(),
  observations: z.string().nullable(),
  version: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CrmLeadEntity = z.infer<typeof crmLeadSchema>;

const crmLeadDetailSchema = crmLeadSchema.extend({
  saas_product: relationSchema,
  client: relationSchema,
  responsible_user: relationSchema,
});
export type CrmLeadDetailEntity = z.infer<typeof crmLeadDetailSchema>;

const listResponseSchema = z.object({
  items: z.array(crmLeadSchema),
  meta: z.object({ next_cursor: z.string().nullable(), total: z.number() }),
});
export type CrmLeadListResponse = z.infer<typeof listResponseSchema>;

const createResponseSchema = z.object({
  data: crmLeadSchema,
  warnings: z.array(crmLeadDuplicateWarningSchema),
});

const singleResponseSchema = z.object({ data: crmLeadSchema });
const detailResponseSchema = z.object({ data: crmLeadDetailSchema });

export interface CreateCrmLeadResult {
  lead: CrmLeadEntity;
  warnings: CrmLeadDuplicateWarning[];
}

export interface CrmLeadListParams {
  cursor?: string;
  limit?: number;
  status?: CrmLeadEntity['status'];
  temperature?: CrmLeadEntity['temperature'];
  origin?: CrmLeadEntity['origin'];
  responsible_user_id?: string;
  saas_product_id?: string;
  overdue_contact?: boolean;
  q?: string;
}

/** GET /api/crm/leads — lista paginada por cursor + filtros. */
export async function fetchCrmLeads(
  params: CrmLeadListParams = {},
  signal?: AbortSignal,
): Promise<CrmLeadListResponse> {
  const { data } = await apiClient.get('/api/crm/leads', { params, signal });
  return listResponseSchema.parse(data);
}

/** GET /api/crm/leads/:id — detalhe com relações. */
export async function fetchCrmLead(id: string, signal?: AbortSignal): Promise<CrmLeadDetailEntity> {
  const { data } = await apiClient.get(`/api/crm/leads/${id}`, { signal });
  return detailResponseSchema.parse(data).data;
}

/** POST /api/crm/leads — cria lead. Retorna lead + warnings de dedup (FR-50.9). */
export async function createCrmLead(payload: CreateCrmLeadDto): Promise<CreateCrmLeadResult> {
  const { data } = await apiClient.post('/api/crm/leads', payload);
  const parsed = createResponseSchema.parse(data);
  return { lead: parsed.data, warnings: parsed.warnings };
}

/** PUT /api/crm/leads/:id — atualiza com optimistic lock (version no body). */
export async function updateCrmLead(id: string, payload: UpdateCrmLeadDto): Promise<CrmLeadEntity> {
  const { data } = await apiClient.put(`/api/crm/leads/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}

/** POST /api/crm/leads/:id/discard — soft discard com motivo obrigatório. */
export async function discardCrmLead(
  id: string,
  payload: DiscardCrmLeadDto,
): Promise<CrmLeadEntity> {
  const { data } = await apiClient.post(`/api/crm/leads/${id}/discard`, payload);
  return singleResponseSchema.parse(data).data;
}
